import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Dropdown from "../components/inputs/Dropdown";
import Input from "../components/inputs/Input";
import MultiSelectDropdown from "../components/inputs/MultiSelectDropdown";
import YesNoRadioGroup from "../components/inputs/YesNoRadioGroup";
import PROJECT_CONFIG from "../config/projectConfig";
import { useAuth } from "../hooks/useAuth";
import {
  nextStep,
  prevStep,
  resetForm,
  setError,
  setLoading,
  updateFormData,
} from "../redux/applicationFormReducer";
import { cadreAPI } from "../services/api";
import { newCadreApplicationSchema } from "../types/schemas";
import {
  EDUCATION_OPTIONS,
  EXPERTISE_OPTIONS,
  FEMA_REGION_OPTIONS,
  NFIP_WORK_LEVEL_OPTIONS,
  PTS_JOINT_VENTURE_OPTIONS,
  US_STATES,
  WORK_LEVEL_OPTIONS,
  YES_NO_ONLY_OPTIONS,
} from "../utils/constants";

const CadreApplication = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { currentStep, formData, loading } = useSelector(
    (state) => state.applicationForm
  );
  const [existingApplication, setExistingApplication] = useState(null);

  // React Hook Form setup with Zod validation
  const { control, handleSubmit, watch, setValue, trigger } = useForm({
    resolver: zodResolver(newCadreApplicationSchema),
    defaultValues: {
      applicantName: formData.applicantName || "",
      email: formData.email || "",
      phone: formData.phone || "",
      education: formData.education || "",
      shortBio: formData.shortBio || "",
      expertise: formData.expertise || [],
      yearsExperience: formData.yearsExperience || "",
      certifications: formData.certifications || "",
      resume: formData.resume || null,
      coverLetter: formData.coverLetter || "",
      notes: formData.notes || "",
      conflictOfInterest: {
        employeeOfFemaOrDhs:
          formData.conflictOfInterest?.employeeOfFemaOrDhs || "",
        retiredEmployeeOfFemaOrDhs:
          formData.conflictOfInterest?.retiredEmployeeOfFemaOrDhs || "",
        currentEmployeeOrContractorOfFEMA_PTS:
          formData.conflictOfInterest?.currentEmployeeOrContractorOfFEMA_PTS ||
          "",
        ptsJointVenture: formData.conflictOfInterest?.ptsJointVenture || "",
        workLevel: formData.conflictOfInterest?.workLevel || "",
        regions: formData.conflictOfInterest?.regions || [],
        states: formData.conflictOfInterest?.states || [],
        localCommunityName:
          formData.conflictOfInterest?.localCommunityName || "",
        pastEmployeeOrContractor:
          formData.conflictOfInterest?.pastEmployeeOrContractor || "",
        nfipInvolvement: formData.conflictOfInterest?.nfipInvolvement || "",
        nfipWorkLevel: formData.conflictOfInterest?.nfipWorkLevel || "",
        nfipDetails: formData.conflictOfInterest?.nfipDetails || "",
      },
    },
    mode: "onChange",
  });

  // Watch specific fields for conditional rendering
  const watchedCurrentEmployee = watch(
    "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
  );
  const watchedWorkLevel = watch("conflictOfInterest.workLevel");
  const watchedNfipInvolvement = watch("conflictOfInterest.nfipInvolvement");

  const expertiseOptions = EXPERTISE_OPTIONS;
  const educationOptions = EDUCATION_OPTIONS;
  const yesNoOnlyOptions = YES_NO_ONLY_OPTIONS;
  const ptsJointVentureOptions = PTS_JOINT_VENTURE_OPTIONS;
  const workLevelOptions = WORK_LEVEL_OPTIONS;
  const nfipWorkLevelOptions = NFIP_WORK_LEVEL_OPTIONS;

  const regions = FEMA_REGION_OPTIONS;

  const states = ["None", ...US_STATES];

  const checkExistingApplication = useCallback(async () => {
    if (!user) return;

    try {
      dispatch(setLoading(true));
      const response = await cadreAPI.getUserApplication();

      if (response.success && response.application) {
        setExistingApplication(response.application);
      }
    } catch (error) {
      console.error("Error checking existing application:", error);
      dispatch(setError("Failed to check existing application"));
    } finally {
      dispatch(setLoading(false));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (user) {
      dispatch(
        updateFormData({
          applicantName: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
        })
      );
      checkExistingApplication();
    }
  }, [user, dispatch, checkExistingApplication]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or Word document");
        e.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size must be less than 5MB");
        e.target.value = "";
        return;
      }

      setValue("resume", file);
      dispatch(updateFormData({ resume: file }));
      toast.success("Resume selected successfully");
    }
  };

  const handleNextStep = async () => {
    // Define field names for each step
    const stepFields = {
      1: ["applicantName", "email", "education", "shortBio"],
      2: ["expertise"],
      3: ["yearsExperience", "resume"],
      4: [
        "conflictOfInterest.employeeOfFemaOrDhs",
        "conflictOfInterest.retiredEmployeeOfFemaOrDhs",
        "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS",
        "conflictOfInterest.pastEmployeeOrContractor",
        "conflictOfInterest.nfipInvolvement",
      ],
    };

    // Get fields to validate for current step
    let fieldsToValidate = stepFields[currentStep] || [];

    // For step 4, add conditional fields based on current form values
    if (currentStep === 4) {
      const currentEmployee = watch(
        "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
      );
      const workLevel = watch("conflictOfInterest.workLevel");
      const nfipInvolvement = watch("conflictOfInterest.nfipInvolvement");

      // Add conditional fields for current employee
      if (currentEmployee === "YES") {
        fieldsToValidate.push("conflictOfInterest.ptsJointVenture");
        fieldsToValidate.push("conflictOfInterest.workLevel");

        if (workLevel === "Regional") {
          fieldsToValidate.push("conflictOfInterest.regions");
        }
        if (["Regional", "State", "Local"].includes(workLevel)) {
          fieldsToValidate.push("conflictOfInterest.states");
        }
        if (workLevel === "Local") {
          fieldsToValidate.push("conflictOfInterest.localCommunityName");
        }
      }

      // Add conditional fields for NFIP involvement
      if (nfipInvolvement === "YES") {
        fieldsToValidate.push("conflictOfInterest.nfipWorkLevel");
        fieldsToValidate.push("conflictOfInterest.nfipDetails");
      }
    }

    // Trigger validation for current step fields
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      dispatch(nextStep());
    }
    // If not valid, React Hook Form will show inline errors automatically
  };

  const handlePrevStep = () => {
    dispatch(prevStep());
  };

  const onSubmit = async (data) => {
    try {
      dispatch(setLoading(true));

      // Create FormData for file upload
      const submitData = new FormData();

      // Add all form fields from React Hook Form data
      submitData.append("applicantName", data.applicantName);
      submitData.append("email", data.email);
      submitData.append("phone", data.phone || "");
      submitData.append("education", data.education);
      submitData.append("yearsExperience", data.yearsExperience);
      submitData.append("expertise", JSON.stringify(data.expertise));
      submitData.append("certifications", data.certifications || "");
      submitData.append("coverLetter", data.shortBio || "");
      submitData.append("notes", data.notes || "");

      // Add Conflict of Interest fields individually (not as JSON object)
      if (data.conflictOfInterest) {
        const coi = data.conflictOfInterest;

        // Main questions
        if (coi.employeeOfFemaOrDhs) {
          submitData.append("employeeOfFemaOrDhs", coi.employeeOfFemaOrDhs);
        }
        if (coi.retiredEmployeeOfFemaOrDhs) {
          submitData.append(
            "retiredEmployeeOfFemaOrDhs",
            coi.retiredEmployeeOfFemaOrDhs
          );
        }
        if (coi.currentEmployeeOrContractorOfFEMA_PTS) {
          submitData.append(
            "currentEmployeeOrContractorOfFEMA_PTS",
            coi.currentEmployeeOrContractorOfFEMA_PTS
          );
        }
        if (coi.pastEmployeeOrContractor) {
          submitData.append(
            "pastEmployeeOrContractor",
            coi.pastEmployeeOrContractor
          );
        }
        if (coi.nfipInvolvement) {
          submitData.append("nfipInvolvement", coi.nfipInvolvement);
        }

        // Question 3 follow-up fields
        if (coi.ptsJointVenture) {
          submitData.append("ptsJointVenture", coi.ptsJointVenture);
        }
        if (coi.workLevel) {
          submitData.append("workLevel", coi.workLevel);
        }
        if (
          coi.regions &&
          Array.isArray(coi.regions) &&
          coi.regions.length > 0
        ) {
          submitData.append("regions", JSON.stringify(coi.regions));
        }
        if (coi.states && Array.isArray(coi.states) && coi.states.length > 0) {
          submitData.append("states", JSON.stringify(coi.states));
        }
        if (coi.localCommunityName) {
          submitData.append("localCommunityName", coi.localCommunityName);
        }

        // Question 5 follow-up fields
        if (coi.nfipWorkLevel) {
          submitData.append("nfipWorkLevel", coi.nfipWorkLevel);
        }
        if (coi.nfipDetails) {
          submitData.append("nfipDetails", coi.nfipDetails);
        }
      }

      // Add resume file if exists
      if (data.resume) {
        submitData.append("resume", data.resume);
      }

      // Submit via API
      const response = await cadreAPI.submitApplicationWithFile(submitData);

      if (response.data?.success) {
        toast.success(
          "Application submitted successfully! You will receive an email confirmation."
        );
        dispatch(resetForm());
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      dispatch(
        setError(
          error.response?.data?.message ||
            "Failed to submit application. Please try again."
        )
      );
      toast.error(
        error.response?.data?.message ||
          "Failed to submit application. Please try again."
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Show loading state while checking for existing application
  if (loading && !formData.applicantName) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue mx-auto"
            aria-hidden="true"
          />
          <p className="mt-4 text-gray-700">Loading application...</p>
        </div>
      </div>
    );
  }

  // Show submitted state
  if (existingApplication && existingApplication.status === "SUBMITTED") {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Application Submitted Successfully!
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for applying to join the {PROJECT_CONFIG.name} Cadre.
              Your application has been received and is currently under review.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 bg-custom-blue text-white rounded-lg hover:bg-custom-light-blue transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Cadre Member Application
          </h1>
          <p className="text-gray-600 mt-2">
            Join our expert panel of flood hazard professionals
          </p>
        </div>

        {/* Progress Steps */}
        <nav
          className="bg-white rounded-lg shadow-lg p-8 mb-8"
          aria-label="Application progress"
        >
          <p className="sr-only" aria-live="polite">
            Step {currentStep} of 5
          </p>
          <div className="relative">
            <div
              className="absolute top-5 left-10 right-10 h-0.5 bg-gray-200 rounded-full"
              aria-hidden="true"
            />
            <ol className="flex items-center justify-between relative list-none p-0 m-0">
            {[
              { step: 1, label: "Personal Info" },
              { step: 2, label: "Expertise" },
              { step: 3, label: "Experience" },
              { step: 4, label: "Conflict of Interest" },
              { step: 5, label: "Review" },
            ].map(({ step, label }) => (
              <li
                key={step}
                className="flex flex-col items-center relative z-10"
                aria-current={currentStep === step ? "step" : undefined}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                    currentStep >= step
                      ? "bg-custom-blue text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  aria-hidden="true"
                >
                  {currentStep > step ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    step
                  )}
                </div>
                <div className="mt-3 text-center">
                  <span
                    className={`text-xs font-medium transition-colors duration-300 ${
                      currentStep >= step ? "text-custom-blue" : "text-gray-700"
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </li>
            ))}
            </ol>
          </div>
        </nav>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Personal Information
                </h2>
                <p className="text-gray-600">
                  Please provide your basic information to get started
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Controller
                  name="applicantName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Full Name"
                      field={field}
                      required
                      placeholder="Dr. John Smith"
                      error={fieldState.error}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Email Address"
                      field={field}
                      required
                      type="email"
                      placeholder="john.smith@example.com"
                      error={fieldState.error}
                    />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Phone Number"
                      field={field}
                      type="tel"
                      placeholder="(555) 123-4567"
                      error={fieldState.error}
                    />
                  )}
                />

                <Controller
                  name="education"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      options={educationOptions}
                      onSelect={field.onChange}
                      value={field.value}
                      label="Select Education Level"
                      headLabel="Highest Education"
                      required
                      error={fieldState.error}
                    />
                  )}
                />
              </div>

              <Controller
                name="shortBio"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <label
                      htmlFor="shortBio"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Short Bio <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...field}
                      id="shortBio"
                      rows={4}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent ${
                        fieldState.error ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Brief description of your background and experience in flood hazard analysis..."
                    />
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Step 2: Expertise Areas */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Areas of Expertise
                </h2>
                <p className="text-gray-600">
                  Select all areas that apply (minimum 1 required)
                </p>
              </div>

              <Controller
                name="expertise"
                control={control}
                render={({ field, fieldState }) => (
                  <MultiSelectDropdown
                    options={expertiseOptions}
                    selectedValues={field.value}
                    onSelectionChange={field.onChange}
                    placeholder="Select expertise areas"
                    label="Expertise Areas"
                    required
                    error={fieldState.error}
                  />
                )}
              />

              <Controller
                name="certifications"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Professional Certifications
                    </p>
                    <textarea
                      {...field}
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent ${
                        fieldState.error ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="List your professional certifications (e.g., PE, CFM, etc.)"
                    />
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Step 3: Experience & Documents */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Experience & Documents
                </h2>
                <p className="text-gray-600">
                  Share your professional experience and upload your resume
                </p>
              </div>

              <Controller
                name="yearsExperience"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Years of Professional Experience"
                    field={field}
                    required
                    type="number"
                    placeholder="Minimum 10 years required"
                    error={fieldState.error}
                  />
                )}
              />

              <Controller
                name="resume"
                control={control}
                render={({ field, fieldState }) => {
                  const resumeErrorId = "resume-upload-error";
                  const resumeHelpId = "resume-upload-help";
                  return (
                  <div>
                    <label
                      htmlFor="resume-upload"
                      className="block text-sm font-medium text-gray-900 mb-2"
                    >
                      Upload CV/Resume{" "}
                      <span className="text-red-700" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only"> (required)</span>
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        fieldState.error ? "border-red-700" : "border-gray-300"
                      }`}
                    >
                      <svg
                        className="w-12 h-12 text-gray-500 mx-auto mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept=".pdf,.doc,.docx"
                        className="sr-only"
                        id="resume-upload"
                        aria-invalid={!!fieldState.error}
                        aria-describedby={
                          fieldState.error
                            ? `${resumeHelpId} ${resumeErrorId}`
                            : resumeHelpId
                        }
                        aria-required="true"
                      />
                      <label
                        htmlFor="resume-upload"
                        className="cursor-pointer inline-block focus-within:outline-none"
                      >
                        {field.value ? (
                          <div>
                            <p className="text-custom-blue font-medium">
                              {field.value.name}
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                              Click to change file
                            </p>
                          </div>
                        ) : (
                          <>
                            <p className="text-gray-900 font-medium">
                              Click to upload a file
                            </p>
                            <p
                              id={resumeHelpId}
                              className="text-sm text-gray-700 mt-1"
                            >
                              PDF, DOC, or DOCX up to 5MB
                            </p>
                          </>
                        )}
                      </label>
                      {field.value && (
                        <p id={resumeHelpId} className="sr-only">
                          Selected file: {field.value.name}. PDF, DOC, or DOCX
                          up to 5MB.
                        </p>
                      )}
                    </div>
                    {fieldState.error && (
                      <p
                        id={resumeErrorId}
                        role="alert"
                        className="mt-1 text-sm text-red-700"
                      >
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                  );
                }}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <label
                      htmlFor="notes"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Additional Notes
                    </label>
                    <textarea
                      {...field}
                      id="notes"
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent ${
                        fieldState.error ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Any additional information you'd like to share..."
                    />
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Step 4: Conflict of Interest */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Conflict of Interest
                </h2>
                <p className="text-gray-600">
                  Please answer these important questions about potential
                  conflicts
                </p>
              </div>

              {/* Question 1 */}
              <div className="mb-6">
                <Controller
                  name="conflictOfInterest.employeeOfFemaOrDhs"
                  control={control}
                  render={({ field, fieldState }) => (
                    <YesNoRadioGroup
                      name="employeeOfFemaOrDhs"
                      value={field.value}
                      onChange={field.onChange}
                      options={yesNoOnlyOptions}
                      error={fieldState.error}
                      legend={
                        <>
                          <span className="font-bold text-custom-blue">1.</span>{" "}
                          Are you a current or past employee of FEMA or DHS?
                        </>
                      }
                    />
                  )}
                />
              </div>

              {/* Question 2 */}
              <div className="mb-6">
                <Controller
                  name="conflictOfInterest.retiredEmployeeOfFemaOrDhs"
                  control={control}
                  render={({ field, fieldState }) => (
                    <YesNoRadioGroup
                      name="retiredEmployeeOfFemaOrDhs"
                      value={field.value}
                      onChange={field.onChange}
                      options={yesNoOnlyOptions}
                      error={fieldState.error}
                      legend={
                        <>
                          <span className="font-bold text-custom-blue">2.</span>{" "}
                          Are you a retired employee of FEMA or DHS?
                        </>
                      }
                    />
                  )}
                />
              </div>

              {/* Question 3 */}
              <div className="mb-6">
                <Controller
                  name="conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
                  control={control}
                  render={({ field, fieldState }) => (
                    <YesNoRadioGroup
                      name="currentEmployeeOrContractorOfFEMA_PTS"
                      value={field.value}
                      onChange={field.onChange}
                      options={yesNoOnlyOptions}
                      error={fieldState.error}
                      legend={
                        <>
                          <span className="font-bold text-custom-blue">3.</span>{" "}
                          Current employee or contractor of FEMA/PTS? (Are you a
                          current employee of a firm providing contracting
                          support to FEMA or a firm which is a subcontractor of a
                          Production and Technical Services (PTS) firm?)
                        </>
                      }
                    />
                  )}
                />

                {/* Conditional fields for Question 3 */}
                {watchedCurrentEmployee === "YES" && (
                  <div className="mt-4 ml-6 space-y-4">
                    <Controller
                      name="conflictOfInterest.ptsJointVenture"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Which PTS joint venture do/did you or your firm work
                            with?
                          </p>
                          <Dropdown
                            options={ptsJointVentureOptions}
                            onSelect={field.onChange}
                            value={field.value}
                            label="Select PTS Joint Venture"
                            required
                            error={fieldState.error}
                          />
                        </div>
                      )}
                    />

                    <Controller
                      name="conflictOfInterest.workLevel"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Are you, or were you, working at the National,
                            Regional, State or Local Level?
                          </p>
                          <Dropdown
                            options={workLevelOptions}
                            onSelect={field.onChange}
                            value={field.value}
                            label="Select Work Level"
                            required
                            error={fieldState.error}
                          />
                        </div>
                      )}
                    />

                    {/* Regions (only if Regional) */}
                    {watchedWorkLevel === "Regional" && (
                      <Controller
                        name="conflictOfInterest.regions"
                        control={control}
                        render={({ field, fieldState }) => (
                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">
                              Select the regions in which you worked
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              {regions.map((region) => (
                                <label
                                  key={region}
                                  className="flex items-center"
                                >
                                  <input
                                    type="checkbox"
                                    checked={field.value.includes(region)}
                                    onChange={(e) => {
                                      const newValues = e.target.checked
                                        ? [...field.value, region]
                                        : field.value.filter(
                                            (r) => r !== region
                                          );
                                      field.onChange(newValues);
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-gray-700">
                                    Region {region}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {fieldState.error && (
                              <p className="mt-1 text-sm text-red-600">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    )}

                    {/* States (if Regional OR State OR Local) */}
                    {(watchedWorkLevel === "Regional" ||
                      watchedWorkLevel === "State" ||
                      watchedWorkLevel === "Local") && (
                      <Controller
                        name="conflictOfInterest.states"
                        control={control}
                        render={({ field, fieldState }) => (
                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">
                              Select the states in which you worked
                            </p>
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-3">
                              <div className="grid grid-cols-2 gap-2">
                                {states.map((state) => (
                                  <label
                                    key={state}
                                    className="flex items-center"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={field.value.includes(state)}
                                      onChange={(e) => {
                                        const newValues = e.target.checked
                                          ? [...field.value, state]
                                          : field.value.filter(
                                              (s) => s !== state
                                            );
                                        field.onChange(newValues);
                                      }}
                                      className="mr-2"
                                    />
                                    <span className="text-gray-700 text-sm">
                                      {state}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            {fieldState.error && (
                              <p className="mt-1 text-sm text-red-600">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    )}

                    {/* Local Community Name */}
                    {watchedWorkLevel === "Local" && (
                      <Controller
                        name="conflictOfInterest.localCommunityName"
                        control={control}
                        render={({ field, fieldState }) => (
                          <div>
                            <Input
                              label="Enter your community name"
                              field={field}
                              required
                              placeholder="Enter community name"
                              error={fieldState.error}
                            />
                          </div>
                        )}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Question 4 */}
              <div className="mb-6">
                <Controller
                  name="conflictOfInterest.pastEmployeeOrContractor"
                  control={control}
                  render={({ field, fieldState }) => (
                    <YesNoRadioGroup
                      name="pastEmployeeOrContractor"
                      value={field.value}
                      onChange={field.onChange}
                      options={yesNoOnlyOptions}
                      error={fieldState.error}
                      legend={
                        <>
                          <span className="font-bold text-custom-blue">4.</span>{" "}
                          Past employee or contractor of FEMA/PTS
                        </>
                      }
                    />
                  )}
                />
              </div>

              {/* Question 5 */}
              <div className="mb-6">
                <Controller
                  name="conflictOfInterest.nfipInvolvement"
                  control={control}
                  render={({ field, fieldState }) => (
                    <YesNoRadioGroup
                      name="nfipInvolvement"
                      value={field.value}
                      onChange={field.onChange}
                      options={yesNoOnlyOptions}
                      error={fieldState.error}
                      legend={
                        <>
                          <span className="font-bold text-custom-blue">5.</span>{" "}
                          Are you currently involved, or have you ever been
                          involved, in an NFIP related flood mapping study other
                          than PTS Contracting?
                        </>
                      }
                    />
                  )}
                />

                {/* NFIP Work Level and Description */}
                {watchedNfipInvolvement === "YES" && (
                  <div className="mt-4 ml-6 space-y-4">
                    <Controller
                      name="conflictOfInterest.nfipWorkLevel"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Are you, or were you, working at the Federal, State
                            or Local level?
                          </p>
                          <Dropdown
                            options={nfipWorkLevelOptions}
                            onSelect={field.onChange}
                            value={field.value}
                            label="Select NFIP Work Level"
                            required
                            error={fieldState.error}
                          />
                        </div>
                      )}
                    />

                    <Controller
                      name="conflictOfInterest.nfipDetails"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-2">
                            Please describe your involvement in a flood study
                            and indicate the name of the local, state or federal
                            agency initiative for which you worked and the date
                            of the study.{" "}
                          </p>

                          <textarea
                            {...field}
                            rows={4}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent ${
                              fieldState.error
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Please describe your involvement ....."
                          />
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Review Your Application
                </h2>
                <p className="text-gray-600">
                  Please review all information before submitting your
                  application
                </p>
              </div>

              <div className="space-y-6">
                {/* Personal Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Name:</strong> {watch("applicantName")}
                    </div>
                    <div>
                      <strong>Email:</strong> {watch("email")}
                    </div>
                    <div>
                      <strong>Phone:</strong> {watch("phone") || "Not provided"}
                    </div>
                    <div>
                      <strong>Education:</strong> {watch("education")}
                    </div>
                    <div className="md:col-span-2">
                      <strong>Bio:</strong> {watch("shortBio")}
                    </div>
                  </div>
                </div>

                {/* Expertise */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Expertise Areas
                  </h3>
                  <div className="text-sm">
                    <div>
                      <strong>Areas:</strong>{" "}
                      {watch("expertise")?.join(", ") || "None selected"}
                    </div>
                    {watch("certifications") && (
                      <div className="mt-2">
                        <strong>Certifications:</strong>{" "}
                        {watch("certifications")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Experience */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Experience & Documents
                  </h3>
                  <div className="text-sm">
                    <div>
                      <strong>Years of Experience:</strong>{" "}
                      {watch("yearsExperience")}
                    </div>
                    <div>
                      <strong>Resume:</strong>{" "}
                      {watch("resume") ? watch("resume").name : "Not uploaded"}
                    </div>
                    {watch("notes") && (
                      <div className="mt-2">
                        <strong>Additional Notes:</strong> {watch("notes")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Conflict of Interest */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Conflict of Interest
                  </h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <strong>1. Current/Past FEMA/DHS Employee:</strong>{" "}
                      {watch("conflictOfInterest.employeeOfFemaOrDhs")}
                    </div>
                    <div>
                      <strong>2. Retired FEMA/DHS Employee:</strong>{" "}
                      {watch("conflictOfInterest.retiredEmployeeOfFemaOrDhs")}
                    </div>
                    <div>
                      <strong>3. Current FEMA/PTS Contractor:</strong>{" "}
                      {watch(
                        "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
                      )}
                    </div>
                    {watch(
                      "conflictOfInterest.currentEmployeeOrContractorOfFEMA_PTS"
                    ) === "YES" && (
                      <>
                        <div>
                          <strong>PTS Joint Venture:</strong>{" "}
                          {watch("conflictOfInterest.ptsJointVenture")}
                        </div>
                        <div>
                          <strong>Work Level:</strong>{" "}
                          {watch("conflictOfInterest.workLevel")}
                        </div>
                        {watch("conflictOfInterest.regions")?.length > 0 && (
                          <div>
                            <strong>Regions:</strong>{" "}
                            {watch("conflictOfInterest.regions").join(", ")}
                          </div>
                        )}
                        {watch("conflictOfInterest.states")?.length > 0 && (
                          <div>
                            <strong>States:</strong>{" "}
                            {watch("conflictOfInterest.states").join(", ")}
                          </div>
                        )}
                        {watch("conflictOfInterest.localCommunityName") && (
                          <div>
                            <strong>Community Name:</strong>{" "}
                            {watch("conflictOfInterest.localCommunityName")}
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <strong>4. Past FEMA/PTS Contractor:</strong>{" "}
                      {watch("conflictOfInterest.pastEmployeeOrContractor")}
                    </div>
                    <div>
                      <strong>5. NFIP Involvement:</strong>{" "}
                      {watch("conflictOfInterest.nfipInvolvement")}
                    </div>
                    {watch("conflictOfInterest.nfipInvolvement") === "YES" && (
                      <>
                        <div>
                          <strong>NFIP Work Level:</strong>{" "}
                          {watch("conflictOfInterest.nfipWorkLevel")}
                        </div>
                        {watch("conflictOfInterest.nfipDetails") && (
                          <div>
                            <strong>NFIP Details:</strong>{" "}
                            {watch("conflictOfInterest.nfipDetails")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200">
            <div className="flex space-x-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="px-6 py-3 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue"
                >
                  Previous
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => navigate("/about")}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
              >
                Cancel
              </button>

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-8 py-3 bg-custom-blue text-white rounded-lg hover:bg-custom-light-blue transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading}
                  className={`px-8 py-3 text-white rounded-lg transition-all duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-custom-blue ${
                    loading
                      ? "bg-gray-600 cursor-not-allowed"
                      : "bg-custom-blue hover:bg-[#001f38]"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    "Submit Application ✓"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadreApplication;
